import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Project } from "@/types";
import {
  fetchProjectsByIds,
  fetchBatchPlannedInvestments,
  fetchProjectMeasures,
  fetchProjectMedia,
} from "@/services/api";
import { useCustomerProjectsBatches } from "@/hooks/useCustomerProjectsBatches";

export function useDashboardData(accessId: string | undefined) {
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);
  const [batchTitles, setBatchTitles] = useState<Record<string, string>>({});
  const [isLoadingBatchData, setIsLoadingBatchData] = useState(false);

  const {
    customer,
    projects,
    isLoading,
    customerError,
    isError,
    projectToBatchIds,
  } = useCustomerProjectsBatches(accessId || undefined);

  useEffect(() => {
    if (customer?.account?.toTransactions) {
      const batchTitlesMap: Record<string, string> = {};

      customer.account.toTransactions.forEach((transaction) => {
        if (transaction.batch?.id && transaction.batch?.title) {
          batchTitlesMap[transaction.batch.id] = transaction.batch.title;
        }
      });

      setBatchTitles(batchTitlesMap);
    }
  }, [customer]);

  const projectIds = projects.map((p) => p.id);

  const { data: projectsDetails, isLoading: projectDetailsLoading } = useQuery({
    queryKey: ["projects-details", projectIds],
    queryFn: () => fetchProjectsByIds(projectIds),
    enabled: projectIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // If we have a selected project, prefetch its media
  useEffect(() => {
    if (selectedProject) {
      queryClient.prefetchQuery({
        queryKey: ["project-media", selectedProject.id],
        queryFn: () => fetchProjectMedia(selectedProject.id),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [selectedProject, queryClient]);

  const fetchBatchData = async (batchIds: string[], projectId: string) => {
    setIsLoadingBatchData(true);

    try {
      const investmentsResults: Record<string, any> = {};
      const investmentsFetchQueue: string[] = [];

      for (const batchId of batchIds) {
        const cachedData = queryClient.getQueryData([
          "batch-planned-investments-single",
          batchId,
        ]);
        if (cachedData) {
          investmentsResults[batchId] = cachedData;
        } else {
          investmentsFetchQueue.push(batchId);
        }
      }

      if (investmentsFetchQueue.length > 0) {
        const chunkSize = 2;
        for (let i = 0; i < investmentsFetchQueue.length; i += chunkSize) {
          const chunk = investmentsFetchQueue.slice(i, i + chunkSize);

          await Promise.all(
            chunk.map(async (batchId) => {
              try {
                const investments = await fetchBatchPlannedInvestments(batchId);

                queryClient.setQueryData(
                  ["batch-planned-investments-single", batchId],
                  investments,
                );
                investmentsResults[batchId] = investments;
              } catch (error) {
                console.error(
                  `Error fetching investments for batch ${batchId}:`,
                  error,
                );
                investmentsResults[batchId] = [];
              }
            }),
          );

          if (i + chunkSize < investmentsFetchQueue.length) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }

      // Measures belong to the project directly (not to a specific batch/vintage),
      // so they're fetched once per project instead of once per batch.
      let measuresResult: any[] = [];
      const cachedMeasures = queryClient.getQueryData([
        "project-measures-single",
        projectId,
      ]);
      if (cachedMeasures) {
        measuresResult = cachedMeasures as any[];
      } else {
        try {
          measuresResult = await fetchProjectMeasures(projectId);
          queryClient.setQueryData(
            ["project-measures-single", projectId],
            measuresResult,
          );
        } catch (error) {
          console.error(
            `Error fetching measures for project ${projectId}:`,
            error,
          );
          measuresResult = [];
        }
      }

      return {
        investments: investmentsResults,
        measures: measuresResult,
      };
    } catch (error) {
      console.error("Error in batch data fetching:", error);
      return { investments: {}, measures: [] };
    } finally {
      setIsLoadingBatchData(false);
    }
  };

  const [batchPlannedInvestments, setBatchPlannedInvestments] = useState<
    Record<string, any>
  >({});
  const [batchMeasures, setBatchMeasures] = useState<any[]>([]);

  const handleProjectSelect = async (project: Project) => {
    setSelectedProject(project);

    const batchIds =
      (projectToBatchIds && projectToBatchIds[project.id]) || [];
    setSelectedBatchIds(batchIds);

    const { investments, measures } = await fetchBatchData(
      batchIds,
      project.id,
    );
    setBatchPlannedInvestments(investments);
    setBatchMeasures(measures);
  };

  const handleCloseProjectPanel = () => {
    setSelectedProject(null);
    setSelectedBatchIds([]);
  };

  const mapCenter = (() => {
    if (selectedProject?.geom && selectedProject.geom.type === "Point") {
      const pointGeom = selectedProject.geom as GeoJSON.Point;
      const coordinates = pointGeom.coordinates;

      if (coordinates && coordinates.length >= 2) {
        return [coordinates[0], coordinates[1]] as [number, number];
      }
    }

    if (projectsDetails && projectsDetails.length > 0) {
      const projectWithCoords = projectsDetails.find(
        (project) => project.geom && project.geom.type === "Point",
      );

      if (projectWithCoords) {
        if (projectWithCoords.geom?.type !== "Point") {
          return [10.385833, 55.360556] as [number, number];
        }

        const pointGeom = projectWithCoords.geom as GeoJSON.Point;
        const coordinates = pointGeom.coordinates;

        if (coordinates && coordinates.length >= 2) {
          return [coordinates[0], coordinates[1]] as [number, number];
        }
      }
    }
    return [10.385833, 55.360556] as [number, number];
  })();

  return {
    customer,
    projects,
    projectsDetails,
    isLoading: isLoading || projectDetailsLoading,
    isLoadingBatchData,
    isError,
    customerError,
    batchPlannedInvestments,
    batchMeasures,
    batchTitles,
    selectedProject,
    selectedBatchIds,
    mapCenter,
    handleProjectSelect,
    handleCloseProjectPanel,
    projectToBatchIds,
  };
}
